import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { useConfirm } from "../contexts/ConfirmProvider";
import { useNavigate } from "react-router-dom";
import { extractError } from "../lib/errors";
import { motion, AnimatePresence } from "framer-motion";
import { TableSkeleton } from "../components/skeletons";
import { EmptyState } from "../components/EmptyState";
import { PageHeader } from "../components/PageHeader";
import {
  ClipboardCheck, Plus, Calendar, Users, Play, CheckCircle2,
  XCircle, AlertTriangle, ChevronRight, Trash2
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { FRAMEWORKS as ALL_FRAMEWORKS, AUDITABLE_FRAMEWORKS } from "../constants/frameworks";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter
} from "../components/ui/sheet";
import { toast } from "sonner";

const Audits = () => {
  const { token } = useAuth();
  const { t } = useI18n();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [audits, setAudits] = useState([]);
  const [nonConformities, setNonConformities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddAudit, setShowAddAudit] = useState(false);
  const [showAddNC, setShowAddNC] = useState(false);

  const [newAudit, setNewAudit] = useState({
    title: "",
    frameworks: [],
    scope_description: "",
    lead_auditor: "",
    team_members: [],
    planned_start: "",
    planned_end: "",
    locale: "es",
  });

  const [newNC, setNewNC] = useState({
    audit_id: "",
    control_id: "",
    nc_type: "minor",
    description: "",
    root_cause: "",
    corrective_action: "",
    assigned_to: "",
    due_date: ""
  });

  const frameworks = ALL_FRAMEWORKS.map((f) => ({ value: f.id, label: f.short, color: f.color }));
  // S18 (D-S17-001, G6): audit-creation picker offers only ISO 19011-auditable
  // frameworks — NIST CSF 2.0 / ISO 31000 / ISO 22301 are cross-mapping targets,
  // not auditable criteria. `frameworks` above (full list) still backs the
  // read-only badge lookups elsewhere in this page/tabs.
  const pickerFrameworks = AUDITABLE_FRAMEWORKS.map((f) => ({ value: f.id, label: f.short, color: f.color }));

  const auditStatuses = [
    { value: "planned", label: "Planned", color: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300" },
    { value: "in_progress", label: "In Progress", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
    { value: "completed", label: "Completed", color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" },
    { value: "cancelled", label: "Cancelled", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" }
  ];

  const ncTypes = [
    { value: "minor", label: "Minor NC", color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400" },
    { value: "major", label: "Major NC", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" },
    { value: "opportunity", label: "Opportunity for Improvement", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" }
  ];

  const checklistResults = [
    { value: "not_assessed", label: "Not Assessed", icon: null },
    { value: "compliant", label: "Compliant", icon: CheckCircle2, color: "text-green-500" },
    { value: "non_compliant", label: "Non-Compliant", icon: XCircle, color: "text-red-500" },
    { value: "observation", label: "Observation", icon: AlertTriangle, color: "text-yellow-500" },
    { value: "not_applicable", label: "N/A", icon: null }
  ];

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [auditsRes, ncsRes] = await Promise.all([
        axios.get(`${API}/audits`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/non-conformities`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setAudits(auditsRes.data);
      setNonConformities(ncsRes.data);
    } catch (error) {
      console.error("Failed to fetch audits:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAudit = async (auditId, evt) => {
    if (evt) evt.stopPropagation();
    if (!(await confirm({ title: t("audits.delete_title"), description: t("audits.delete_confirm"), destructive: true }))) return;
    try {
      await axios.delete(`${API}/audits/${auditId}`, { headers: { Authorization: `Bearer ${token}` } });
      setAudits((prev) => prev.filter((a) => a.audit_id !== auditId));
    } catch (e) {
      console.error("Delete failed", e);
    }
  };

  const createAudit = async () => {
    if (!newAudit.title) {
      toast.error(t("audits.toast.title_required"));
      return;
    }

    try {
      await axios.post(`${API}/audits`, newAudit, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t("audits.toast.created"));
      setShowAddAudit(false);
      setNewAudit({
        title: "",
        frameworks: [],
        scope_description: "",
        lead_auditor: "",
        team_members: [],
        planned_start: "",
        planned_end: "",
        locale: "es",
      });
      fetchData();
    } catch (error) {
      toast.error(extractError(error, t("audits.toast.create_failed")));
    }
  };

  const createNC = async () => {
    if (!newNC.description) {
      toast.error(t("audits.toast.desc_required"));
      return;
    }

    try {
      await axios.post(`${API}/non-conformities`, newNC, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t("audits.toast.nc_created"));
      setShowAddNC(false);
      setNewNC({
        audit_id: "",
        control_id: "",
        nc_type: "minor",
        description: "",
        root_cause: "",
        corrective_action: "",
        assigned_to: "",
        due_date: ""
      });
      fetchData();
    } catch (error) {
      toast.error(extractError(error, t("audits.toast.nc_failed")));
    }
  };

  const getStatusBadge = (status, statuses) => {
    return statuses.find(s => s.value === status)?.color || "";
  };

  if (loading) {
    return <TableSkeleton rows={6} columns={5} label="Loading audits" />;
  }

  return (
    <div className="p-6 space-y-6" data-testid="audits-page">
      <PageHeader
        title={t("audits.title")}
        description={t("audits.subtitle")}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => { setNewNC({ ...newNC, audit_id: "" }); setShowAddNC(true); }}>
              <AlertTriangle className="w-4 h-4 mr-2" />
              {t("audits.log_nc_btn")}
            </Button>
            <Button onClick={() => setShowAddAudit(true)} data-testid="add-audit-btn">
              <Plus className="w-4 h-4 mr-2" />
              {t("audits.new")}
            </Button>
          </div>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-border">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                <ClipboardCheck className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">{audits.length}</p>
                <p className="text-xs text-muted-foreground">{t("total")} {t("audits.title")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Play className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">
                  {audits.filter(a => a.status === "in_progress").length}
                </p>
                <p className="text-xs text-muted-foreground">{t("audits.status.in_progress")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">
                  {nonConformities.filter(nc => nc.nc_type === "major").length}
                </p>
                <p className="text-xs text-muted-foreground">{t("audits.nc.major")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">
                  {nonConformities.filter(nc => nc.status === "open").length}
                </p>
                <p className="text-xs text-muted-foreground">{t("open")} {t("audits.nc")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="audits" className="space-y-4">
        <TabsList>
          <TabsTrigger value="audits" data-testid="audits-tab">{t("audits.tab_audits")}</TabsTrigger>
          <TabsTrigger value="ncs" data-testid="ncs-tab">{t("audits.tab_nc")}</TabsTrigger>
        </TabsList>

        <TabsContent value="audits">
          <ScrollArea className="h-[calc(100vh-28rem)]">
            <div className="space-y-3 pr-4">
              <AnimatePresence mode="popLayout">
                {audits.map((audit, index) => (
                  <motion.div
                    key={audit.audit_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2, delay: index * 0.02 }}
                  >
                    <Card
                      className="border-border hover:border-primary/50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/audits/${audit.audit_id}`)}
                      data-testid={`audit-card-${audit.audit_id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <Badge variant="outline" className={getStatusBadge(audit.status, auditStatuses)}>
                                {t(`audits.status.${audit.status}`) || audit.status}
                              </Badge>
                              {audit.frameworks?.map(fw => (
                                <Badge key={fw} variant="secondary" className="text-xs">
                                  {frameworks.find(f => f.value === fw)?.label || fw}
                                </Badge>
                              ))}
                            </div>
                            <h3 className="font-semibold mb-1">{audit.title}</h3>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              {audit.lead_auditor && (
                                <span className="flex items-center gap-1">
                                  <Users className="w-4 h-4" />
                                  {audit.lead_auditor}
                                </span>
                              )}
                              {audit.planned_start && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  {new Date(audit.planned_start).toLocaleDateString()}
                                </span>
                              )}
                              {audit.checklist?.length > 0 && (
                                <span>{t("audits.checklist_count").replace("{count}", audit.checklist.length)}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-muted-foreground hover:text-destructive"
                              onClick={(e) => handleDeleteAudit(audit.audit_id, e)}
                              aria-label="Eliminar auditoría"
                              data-testid={`delete-audit-${audit.audit_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                            <ChevronRight className="w-5 h-5 text-muted-foreground" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </AnimatePresence>

              {audits.length === 0 && (
                <EmptyState
                  icon={ClipboardCheck}
                  title={t("audits.no_audits_title")}
                  description={t("audits.no_audits_desc")}
                  action={
                    <Button onClick={() => setShowAddAudit(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      {t("audits.create_btn")}
                    </Button>
                  }
                />
              )}
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="ncs">
          <ScrollArea className="h-[calc(100vh-28rem)]">
            <div className="space-y-3 pr-4">
              <AnimatePresence mode="popLayout">
                {nonConformities.map((nc, index) => (
                  <motion.div
                    key={nc.nc_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2, delay: index * 0.02 }}
                  >
                    <Card className="border-border" data-testid={`nc-card-${nc.nc_id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <Badge variant="outline" className={getStatusBadge(nc.nc_type, ncTypes)}>
                                {t(`audits.nc.${nc.nc_type}`) || nc.nc_type}
                              </Badge>
                              <Badge variant="secondary" className="capitalize">
                                {nc.status}
                              </Badge>
                            </div>
                            <p className="text-sm mb-2">{nc.description}</p>
                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                              {nc.assigned_to && <span>{t("audits.nc_assigned").replace("{name}", nc.assigned_to)}</span>}
                              {nc.due_date && <span>{t("audits.nc_due").replace("{date}", new Date(nc.due_date).toLocaleDateString())}</span>}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </AnimatePresence>

              {nonConformities.length === 0 && (
                <EmptyState
                  icon={CheckCircle2}
                  title={t("audits.no_ncs_title")}
                  description={t("audits.no_ncs_desc")}
                />
              )}
            </div>
          </ScrollArea>
        </TabsContent>
      </Tabs>

      {/* Create Audit Sheet */}
      <Sheet open={showAddAudit} onOpenChange={setShowAddAudit}>
        <SheetContent side="right" className="w-full sm:max-w-xl flex flex-col gap-0 p-0">
          <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <SheetTitle>{t("audits.new_title")}</SheetTitle>
            <SheetDescription>{t("audits.new_desc")}</SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t("title")} *</Label>
              <Input
                value={newAudit.title}
                onChange={(e) => setNewAudit({ ...newAudit, title: e.target.value })}
                placeholder={t("audits.title_ph")}
                data-testid="audit-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{t("audits.frameworks_label")}</Label>
              <div className="flex flex-wrap gap-2">
                {pickerFrameworks.map(fw => (
                  <Button
                    key={fw.value}
                    variant={newAudit.frameworks.includes(fw.value) ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      const updated = newAudit.frameworks.includes(fw.value)
                        ? newAudit.frameworks.filter(f => f !== fw.value)
                        : [...newAudit.frameworks, fw.value];
                      setNewAudit({ ...newAudit, frameworks: updated });
                    }}
                  >
                    {fw.label}
                  </Button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t("audits.scope_label")}</Label>
              <Textarea
                value={newAudit.scope_description}
                onChange={(e) => setNewAudit({ ...newAudit, scope_description: e.target.value })}
                placeholder={t("description")}
              />
            </div>
            <div className="space-y-2">
              <Label>{t("audits.lead_label")}</Label>
              <Input
                value={newAudit.lead_auditor}
                onChange={(e) => setNewAudit({ ...newAudit, lead_auditor: e.target.value })}
                placeholder={t("audits.lead_ph")}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("audits.start_date")}</Label>
                <Input
                  type="date"
                  value={newAudit.planned_start}
                  onChange={(e) => setNewAudit({ ...newAudit, planned_start: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("audits.end_date")}</Label>
                <Input
                  type="date"
                  value={newAudit.planned_end}
                  onChange={(e) => setNewAudit({ ...newAudit, planned_end: e.target.value })}
                />
              </div>
            </div>
          </div>
          </div>
          <SheetFooter className="px-6 py-4 border-t shrink-0">
            <Button variant="outline" onClick={() => setShowAddAudit(false)}>{t("cancel")}</Button>
            <Button onClick={createAudit} data-testid="save-audit-btn">{t("audits.create_btn")}</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Create NC Sheet */}
      <Sheet open={showAddNC} onOpenChange={setShowAddNC}>
        <SheetContent side="right" className="w-full sm:max-w-xl flex flex-col gap-0 p-0">
          <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <SheetTitle>{t("audits.nc.log_title")}</SheetTitle>
            <SheetDescription>{t("audits.nc.log_desc")}</SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t("audits.nc.type")}</Label>
              <Select
                value={newNC.nc_type}
                onValueChange={(v) => setNewNC({ ...newNC, nc_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ncTypes.map(nc => (
                    <SelectItem key={nc.value} value={nc.value}>{nc.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("audits.nc.desc")} *</Label>
              <Textarea
                value={newNC.description}
                onChange={(e) => setNewNC({ ...newNC, description: e.target.value })}
                placeholder={t("audits.nc.desc_ph")}
                data-testid="nc-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{t("audits.nc.root_cause")}</Label>
              <Textarea
                value={newNC.root_cause}
                onChange={(e) => setNewNC({ ...newNC, root_cause: e.target.value })}
                placeholder={t("audits.nc.cause_ph")}
              />
            </div>
            <div className="space-y-2">
              <Label>{t("audits.nc.action")}</Label>
              <Textarea
                value={newNC.corrective_action}
                onChange={(e) => setNewNC({ ...newNC, corrective_action: e.target.value })}
                placeholder={t("audits.nc.action_ph")}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("audits.nc.assigned")}</Label>
                <Input
                  value={newNC.assigned_to}
                  onChange={(e) => setNewNC({ ...newNC, assigned_to: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("audits.nc.due")}</Label>
                <Input
                  type="date"
                  value={newNC.due_date}
                  onChange={(e) => setNewNC({ ...newNC, due_date: e.target.value })}
                />
              </div>
            </div>
          </div>
          </div>
          <SheetFooter className="px-6 py-4 border-t shrink-0">
            <Button variant="outline" onClick={() => setShowAddNC(false)}>{t("cancel")}</Button>
            <Button onClick={createNC} data-testid="save-nc-btn">{t("audits.nc.create_btn")}</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default Audits;
